import { useMemo, useRef, useState } from 'react';

import { ArrowDownOutlined } from '@ant-design/icons';
import { Alert, Empty, Input, Radio, Select, Spin, Table } from 'antd';
import { T, TF } from 'react-translator-component';

import { CollapseToggle, useCollapsed } from './collapse';
import DirectionBanner from './DirectionBanner';
import OcpiDrawer, { DRAWER_WIDTH } from './OcpiDrawer';
import {
  FEED_ISSUE_TEXTS,
  feedIssues,
  groupByOperator,
  matchesPartnerStation,
  partnerPoints,
  STATIONS_PAGE_SIZE,
  TONES,
} from './ocpiHelpers';
import OcpiStationMap from './OcpiStationMap';
import PartnerStationPanel from './PartnerStationPanel';
import StatusTag from './StatusTag';
import * as S from './styled';

import { PartnerFeedResult } from '../../../hooks/ocpi';
import { CrossChargeBox } from '../../../types/responses/Ocpi';

type Filter = 'all' | 'inFeed' | 'discarded';
type OperatorRow = { operator: string; total: number; inFeed: number; discarded: number };

const OPERATORS_PAGE_SIZE = 8;


const operatorOf = (station: CrossChargeBox) => station.tenant?.name || '—';

interface Props {
  /** Carregado pelo OcpiPage, que também usa na visão geral e na contagem da aba. */
  result?: PartnerFeedResult;
}

/**
 * Estações que o tenant logado recebe como parceiro (eMSP), pela mesma consulta ao cross que o
 * mk-ms-ocpi-java faz para montar o `/locations` dele. Para um CPO sem vínculo como origem, vem vazio.
 *
 * Com muitos operadores, a tabela por operador vira o filtro da lista: busca, paginação, ordenação
 * e clique na linha para ver só as estações daquele operador.
 */
const PartnerFeedTab = ({ result }: Props) => {
  const [filter, setFilter] = useState<Filter>('all');
  const [operator, setOperator] = useState<string>();
  const [collapsed, toggleCollapsed] = useCollapsed('ocpi-partner-operators-collapsed');
  const [operatorQuery, setOperatorQuery] = useState('');
  const [query, setQuery] = useState('');
  const [view, setView] = useState<'list' | 'map'>('list');
  const [selectedId, setSelectedId] = useState<string>();
  const listRef = useRef<HTMLDivElement>(null);

  const stations = useMemo(() => (result?.status === 'ok' ? result.stations : []), [result]);
  const selected = stations.find((s) => s.charge_box_id === selectedId);
  const inFeed = useMemo(() => stations.filter((s) => !feedIssues(s).length), [stations]);
  const operators: OperatorRow[] = useMemo(
    () => groupByOperator(stations).map((g) => ({ ...g, discarded: g.total - g.inFeed })),
    [stations],
  );
  const visibleOperators = operators.filter((o) =>
    o.operator.toLowerCase().includes(operatorQuery.trim().toLowerCase()),
  );

  const rows = stations.filter((s) => {
    if (operator && operatorOf(s) !== operator) return false;
    if (!matchesPartnerStation(s, query)) return false;
    if (filter === 'all') return true;
    return filter === 'inFeed' ? !feedIssues(s).length : feedIssues(s).length > 0;
  });

  const toggleOperator = (name: string) => {
    const next = name === operator ? undefined : name;
    setOperator(next);
    if (next) listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!result) {
    return (
      <S.Panel style={{ textAlign: 'center', padding: 48 }}>
        <Spin />
      </S.Panel>
    );
  }

  if (result.status === 'error') {
    return (
      <Alert
        type="error"
        showIcon
        message={`${TF('Could not load the stations received as a partner.')}${result.httpStatus ? ` (HTTP ${result.httpStatus})` : ''}`}
      />
    );
  }

  if (result.status === 'no-external-id') {
    return (
      <S.Panel>
        <Empty description={T('This account has no external id in the cross, so it does not receive stations as a partner.')} />
      </S.Panel>
    );
  }

  return (
    <>
      <DirectionBanner
        icon={<ArrowDownOutlined />}
        title={T('Receiving as partner (eMSP)')}
        text={
          <>
            {T('Stations of other operators bound to this account in the cross. It is the same list the OCPI service uses to build the partner /locations.')}{' '}
            {T('external id')} <S.Mono>{result.tenant.tenant_external_id}</S.Mono>
          </>
        }
      />

      <S.Grid columns={3}>
        <S.Kpi type="button" onClick={() => setFilter('all')}>
          <span className="label">{T('Stations received')}</span>
          <span className="value">{stations.length}</span>
          <span className="sub">
            {operators.length} {T('operators')}
          </span>
        </S.Kpi>
        <S.Kpi type="button" onClick={() => setFilter('inFeed')}>
          <span className="label">{T('Go into the feed')}</span>
          <span className="value">{inFeed.length}</span>
          <span className="sub">{T('with address, coordinates and operator')}</span>
        </S.Kpi>
        <S.Kpi type="button" onClick={() => setFilter('discarded')}>
          <span className="label">{T('Discarded by the OCPI')}</span>
          <span className="value">{stations.length - inFeed.length}</span>
          <span className="sub">{T('never reach the partner')}</span>
        </S.Kpi>
      </S.Grid>

      {result.truncated && (
        <Alert
          type="warning"
          style={{ marginBottom: 12 }}
          message={T('The cross returned more pages than the dashboard reads. Only the first ones are shown.')}
        />
      )}

      {stations.length === 0 ? (
        <S.Panel>
          <Empty description={T('No station is bound to this account as a partner.')} />
        </S.Panel>
      ) : (
        <>
          <S.Panel>
            <S.PanelHeader>
              <div>
                <h2>
                  {T('By operator')} <S.Muted>({operators.length})</S.Muted>
                </h2>
                <S.Muted>
                  {collapsed
                    ? operator
                      ? `${TF('Showing only')} ${operator}`
                      : TF('Minimized. Expand to filter by operator.')
                    : T('Click an operator to see only its stations.')}
                </S.Muted>
              </div>
              <span style={{ flexGrow: 1 }} />
              {!collapsed && operators.length > OPERATORS_PAGE_SIZE && (
                <Input.Search
                  allowClear
                  placeholder={TF('Search operator')}
                  onChange={(e) => setOperatorQuery(e.target.value)}
                  style={{ width: 260 }}
                />
              )}
              <CollapseToggle collapsed={collapsed} onToggle={toggleCollapsed} />
            </S.PanelHeader>
            {!collapsed && (
            <Table
              size="small"
              rowKey="operator"
              dataSource={visibleOperators}
              pagination={{ pageSize: OPERATORS_PAGE_SIZE, hideOnSinglePage: true, showSizeChanger: false }}
              locale={{ emptyText: T('No operator with this name.') }}
              rowClassName={(row: OperatorRow) =>
                row.operator === operator ? 'ocpi-row-clickable ocpi-row-selected' : 'ocpi-row-clickable'
              }
              onRow={(row: OperatorRow) => ({ onClick: () => toggleOperator(row.operator) })}
              columns={[
                { title: T('Operator'), dataIndex: 'operator', ellipsis: true },
                {
                  title: T('Stations received'),
                  dataIndex: 'total',
                  align: 'right',
                  width: 150,
                  sorter: (a: OperatorRow, b: OperatorRow) => a.total - b.total,
                },
                {
                  title: T('Go into the feed'),
                  dataIndex: 'inFeed',
                  align: 'right',
                  width: 140,
                  sorter: (a: OperatorRow, b: OperatorRow) => a.inFeed - b.inFeed,
                },
                {
                  title: T('Discarded by the OCPI'),
                  dataIndex: 'discarded',
                  align: 'right',
                  width: 170,
                  sorter: (a: OperatorRow, b: OperatorRow) => a.discarded - b.discarded,
                },
                {
                  title: '',
                  width: 140,
                  render: (_: unknown, row: OperatorRow) => (
                    <S.FeedBar title={`${row.inFeed}/${row.total}`}>
                      <span style={{ width: `${(row.inFeed / row.total) * 100}%`, background: TONES.ok.fg }} />
                      <span style={{ width: `${(row.discarded / row.total) * 100}%`, background: TONES.err.fg }} />
                    </S.FeedBar>
                  ),
                },
              ]}
            />
            )}
          </S.Panel>

          <div ref={listRef} />
          <S.Toolbar>
            <Input.Search
              allowClear
              value={query}
              placeholder={TF('Station code, description, address or city')}
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: 300 }}
            />
            <Select
              showSearch
              allowClear
              value={operator}
              placeholder={TF('All operators')}
              onChange={(value) => setOperator(value ? String(value) : undefined)}
              optionFilterProp="label"
              options={operators.map((o) => ({ value: o.operator, label: `${o.operator} (${o.total})` }))}
              style={{ width: 280 }}
            />
            <Radio.Group value={filter} onChange={(e) => setFilter(e.target.value)}>
              <Radio.Button value="all">{T('All')}</Radio.Button>
              <Radio.Button value="inFeed">{T('Go into the feed')}</Radio.Button>
              <Radio.Button value="discarded">{T('Discarded by the OCPI')}</Radio.Button>
            </Radio.Group>
            <span style={{ flexGrow: 1 }} />
            <S.Muted>
              {rows.length} {T('of')} {stations.length} {T('stations')}
            </S.Muted>
            <Radio.Group value={view} onChange={(e) => setView(e.target.value)} buttonStyle="solid">
              <Radio.Button value="list">{T('List')}</Radio.Button>
              <Radio.Button value="map">{T('Map')}</Radio.Button>
            </Radio.Group>
          </S.Toolbar>

          {view === 'map' && (
            <S.Panel>
              <OcpiStationMap
                points={partnerPoints(rows)}
                onSelect={setSelectedId}
              />
              <S.Muted>
                {T('Green goes into the feed, red is discarded by the OCPI. Stations without coordinates do not appear on the map.')}
              </S.Muted>
            </S.Panel>
          )}

          {view === 'list' && (
          <S.Panel>
            <Table
              rowKey="charge_box_id"
              dataSource={rows}
              rowClassName={(s: CrossChargeBox) =>
                s.charge_box_id === selectedId ? 'ocpi-row-clickable ocpi-row-selected' : 'ocpi-row-clickable'
              }
              onRow={(s: CrossChargeBox) => ({
                onClick: () => setSelectedId(s.charge_box_id === selectedId ? undefined : s.charge_box_id),
              })}
              pagination={{ pageSize: STATIONS_PAGE_SIZE, showSizeChanger: false, hideOnSinglePage: true }}
              locale={{ emptyText: T('No station with these filters.') }}
              columns={[
                {
                  title: T('Station'),
                  render: (_: unknown, s: CrossChargeBox) => (
                    <>
                      <div style={{ fontWeight: 500 }}>{s.charge_box_code || s.description || s.charge_box_id}</div>
                      <S.Muted>{s.description && s.charge_box_code ? s.description : s.address?.street || ''}</S.Muted>
                    </>
                  ),
                },
                ...(operator
                  ? []
                  : [{ title: T('Operator'), ellipsis: true, render: (_: unknown, s: CrossChargeBox) => operatorOf(s) }]),
                {
                  title: T('City'),
                  render: (_: unknown, s: CrossChargeBox) =>
                    [s.address?.city, s.address?.state].filter(Boolean).join(' / ') || '—',
                },
                {
                  title: T('Connectors'),
                  align: 'right' as const,
                  render: (_: unknown, s: CrossChargeBox) => (s.connectors || []).length,
                },
                {
                  title: T('Feed'),
                  render: (_: unknown, s: CrossChargeBox) => {
                    const issues = feedIssues(s);
                    return issues.length ? (
                      <>
                        <StatusTag label="Discarded" tone="err" />
                        <div>
                          <S.Muted>{issues.map((i) => TF(FEED_ISSUE_TEXTS[i])).join(' · ')}</S.Muted>
                        </div>
                      </>
                    ) : (
                      <StatusTag label="Goes into the feed" tone="ok" />
                    );
                  },
                },
              ]}
            />
          </S.Panel>
          )}
          <OcpiDrawer
            width={DRAWER_WIDTH}
            visible={!!selected}
            title={selected ? selected.charge_box_code || selected.description || selected.charge_box_id : ''}
            onClose={() => setSelectedId(undefined)}
          >
            {selected && <PartnerStationPanel station={selected} />}
          </OcpiDrawer>
        </>
      )}
    </>
  );
};

export default PartnerFeedTab;
